// Core packages
import {
  Component, Inject,
  OnDestroy, OnInit
} from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { Subscription } from "rxjs";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { IUser } from "src/app/shared/models/user/user.interface";
import { User } from "src/app/shared/models/user/user.model";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";

@Component({
  selector: "app-company-platform-notes-dialog",
  templateUrl: "./company-platform-notes-dialog.component.html",
  styleUrls: ["./company-platform-notes-dialog.component.scss"]
})
export class CompanyPlatformNotesDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  user: User = new User();
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Add subUser form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.confirm"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: true,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true
    }
  };
  // END - Add subUser form

  dialogIsLoading = true;

  title = "";
  text = "";

  notes: any[] = [];
  selectAll = false;

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<CompanyPlatformNotesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public configService: ConfigService,
    private cookieService: CookieService,
    private translateService: TranslateService,
    private authService: AuthService,
    private apiService: ApiService,
  ) {
    dialogRef.disableClose = true;
  }

  /**
   * Init component
   *
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => (this.loggedUser = res))
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    this.title = this.translateService.instant("properties.dialog.companyPlatformNotes.title"); 
    this.text = this.translateService.instant("properties.dialog.companyPlatformNotes.text"); 
    if (
      this.data
    ) {
      if (this.data.title && this.data.title !== '') {
        this.title = this.data.title;
      }
      if (this.data.text && this.data.text !== '') {
        this.text = this.data.text;
      }
    }

    // Init data
    if (
      this.data && 
      this.data.notes &&
      Array.isArray(this.data.notes)
    ) {
      for (const note of this.data.notes) {
        let category = '';
        switch (note.category) {
          case "subscription": 
            category = this.translateService.instant("platformNotes.categories.subscription"); 
          break;
          case "work-account":
            category = this.translateService.instant("platformNotes.categories.work-account");
            break;
          case "technical-operational":
            category = this.translateService.instant("platformNotes.categories.technical-operational");
            break;
          case "insertion":
            category = this.translateService.instant("platformNotes.categories.insertion");
            break;
          case "mapping":
            category = this.translateService.instant("platformNotes.categories.mapping");
            break;
          case "packaging":
            category = this.translateService.instant("platformNotes.categories.packaging");
            break;
          case "tagnfc":
            category = this.translateService.instant("platformNotes.categories.tagnfc");
            break;
          case "suppliers":
            category = this.translateService.instant("platformNotes.categories.suppliers");
            break;
          case "other":
            category = this.translateService.instant("platformNotes.categories.other");
            break;
        }
        this.notes.push({
          id: note._id,
          text: `${category} - ${note.title}`,
          value: false,
        });
      }
    }

    // Stop loading dialog
    this.dialogIsLoading = false;
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(data?: any): void {
    this.dialogRef.close(data);
  }

  /**
   * Handle select signle property
   *
   * @since 1.0.0
   */
  onSelect(checked: boolean): void {
    if (this.notes.some((el: any) => el.value)) {
      this.formBtnOptions.disabled = false;
    } else {
      this.formBtnOptions.disabled = true;
    }
  }
  
  /**
   * Handle select all properties
   *
   * @since 1.0.0
   */
  onSelectAll(checked: boolean): void {
    this.selectAll = checked;
    if (!checked) {
      this.notes.forEach((property: any) => {
        property["value"] = false;
      });
      this.formBtnOptions.disabled = true;
    } else if (checked) {
      this.notes.forEach((property: any) => {
        property["value"] = true;
      });
      this.formBtnOptions.disabled = false;
    }
  }
  
  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(): void {
    const notesSelected: string[] = [];
    if (this.notes && Array.isArray(this.notes)) {
      this.notes.forEach((item: any) => {
        if (item.value) {
          notesSelected.push(item.id);
        }
      })
    }

    this.onClose(notesSelected);
  }
 
}
